import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  useListTestsQuery,
  useCreateTestMutation,
  useUpdateTestMutation,
  useDeleteTestMutation,
  type AdminTest,
  type CreateTestRequest,
  type UpdateTestRequest,
} from "../../../app/services/meseeknaApiV2"
import SimulationEditModal from "./simulation-edit-modal"
import SimulationPreviewModal from "./simulation-preview-modal"

const SimulationManagement: React.FC = () => {
  const navigate = useNavigate()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [selectedTest, setSelectedTest] = useState<AdminTest | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [filterSimType, setFilterSimType] = useState<string>("all")

  const handleEditTutorial = (test: AdminTest) => {
    navigate(`/dashboard/super-admin/sims/${test.id}/tutorial`)
  }

  // RTK Query hooks
  const {
    data: testsData,
    isLoading: testsLoading,
    error: testsError,
    refetch: refetchTests,
  } = useListTestsQuery({ simType: filterSimType !== "all" ? filterSimType : undefined, active: "all" })
  const [createTest, { isLoading: createLoading }] = useCreateTestMutation()
  const [updateTest, { isLoading: updateLoading }] = useUpdateTestMutation()
  const [deleteTest, { isLoading: deleteLoading }] = useDeleteTestMutation()

  const tests = testsData?.data || []

  const handleCreate = () => {
    setIsCreating(true)
    setSelectedTest(null)
    setShowEditModal(true)
  }

  const handleEdit = (test: AdminTest) => {
    setIsCreating(false)
    setSelectedTest(test)
    setShowEditModal(true)
  }

  const handlePreview = (test: AdminTest) => {
    setSelectedTest(test)
    setShowPreviewModal(true)
  }

  const handleDelete = async (test: AdminTest) => {
    if (!window.confirm(`Are you sure you want to delete "${test.title}"?`)) {
      return
    }

    try {
      await deleteTest(test.id).unwrap()
      refetchTests()
    } catch (error) {
      console.error("Error deleting test:", error)
      alert("Failed to delete simulation. Please try again.")
    }
  }

  const handleSave = async (data: CreateTestRequest | UpdateTestRequest) => {
    try {
      if (isCreating) {
        await createTest(data as CreateTestRequest).unwrap()
      } else if (selectedTest) {
        await updateTest({ id: selectedTest.id, data: data as UpdateTestRequest }).unwrap()
      }
      refetchTests()
      setShowEditModal(false)
      setSelectedTest(null)
    } catch (error) {
      console.error("Error saving test:", error)
      throw error // Re-throw to let the modal handle the error
    }
  }

  const getSimTypeLabel = (simType: string | undefined) => {
    switch (simType) {
      case "business":
        return "Business"
      case "geopolitical":
        return "Geopolitical"
      case "custom":
        return "Custom"
      default:
        return "Not Assigned"
    }
  }

  const getSimTypeBadgeColor = (simType: string | undefined) => {
    switch (simType) {
      case "business":
        return "bg-blue-100 text-blue-800"
      case "geopolitical":
        return "bg-purple-100 text-purple-800"
      case "custom":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  return (
    <div className="py-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCreate}
            disabled={createLoading}
            className="bg-indigo-600 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer transition-colors disabled:opacity-50">
            Create Simulation
          </button>

          {/* Filter by sim type */}
          <select
            value={filterSimType}
            onChange={(e) => setFilterSimType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="all">All Types</option>
            <option value="business">Business</option>
            <option value="geopolitical">Geopolitical</option>
            <option value="custom">Custom</option>
            <option value="not_assigned">Not Assigned</option>
          </select>
        </div>

        <div className="text-sm text-gray-500">
          {tests.length} simulation{tests.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Loading state */}
      {testsLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {/* Error state */}
      {testsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="text-red-800 font-medium">Error loading simulations</div>
          <div className="text-red-600 text-sm mt-1">
            {"status" in testsError ? `Error ${testsError.status}` : "Network error"}
          </div>
        </div>
      )}

      {/* Tests table */}
      {!testsLoading && !testsError && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title / Short Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Imported
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tests.map((test) => (
                <tr key={test.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {test.imageData ? (
                      test.imageData.startsWith("<svg") ? (
                        <div
                          className="h-12 w-12 rounded-md overflow-hidden"
                          dangerouslySetInnerHTML={{ __html: test.imageData }}
                        />
                      ) : (
                        <img
                          src={test.imageData}
                          alt={test.title}
                          className="h-12 w-12 rounded-md object-cover"
                        />
                      )
                    ) : (
                      <div className="h-12 w-12 rounded-md bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">No image</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {test.title}
                    </div>
                    <div className="text-sm text-gray-500">
                      {test.shortName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSimTypeBadgeColor(test.simType)}`}>
                      {getSimTypeLabel(test.simType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        test.active === "Y"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                      {test.active === "Y" ? "Active" : "Inactive"}
                    </span>
                    {test.forDemo === "Y" && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Demo
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {test.dateImported
                      ? new Date(test.dateImported).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handlePreview(test)}
                      className="text-indigo-600 hover:text-indigo-900 cursor-pointer">
                      Preview
                    </button>
                    <button
                      onClick={() => handleEdit(test)}
                      className="text-indigo-600 hover:text-indigo-900 cursor-pointer">
                      Edit
                    </button>
                    <button
                      onClick={() => handleEditTutorial(test)}
                      className="text-indigo-600 hover:text-indigo-900 cursor-pointer">
                      Tutorial
                    </button>
                    <button
                      onClick={() => handleDelete(test)}
                      disabled={deleteLoading}
                      className="text-red-600 hover:text-red-900 cursor-pointer disabled:opacity-50">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {tests.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500">
                    No simulations found. Create one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit/Create Modal */}
      {showEditModal && (
        <SimulationEditModal
          test={isCreating ? null : selectedTest}
          isCreating={isCreating}
          onClose={() => {
            setShowEditModal(false)
            setSelectedTest(null)
          }}
          onSave={handleSave}
          isLoading={createLoading || updateLoading}
        />
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedTest && (
        <SimulationPreviewModal
          testId={selectedTest.id}
          onClose={() => {
            setShowPreviewModal(false)
            setSelectedTest(null)
          }}
        />
      )}
    </div>
  )
}

export default SimulationManagement
